import React from 'react';

import confirmAndDeleteAnalysisView from 'analysis-tool/confirmAndDeleteAnalysisView';
import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExperimentOptimizationFeedbackToolbar from 'machine-learning/experiment-optimization/ExperimentOptimizationFeedbackToolbar';
import {assertPipelineStepIsStepType, PipelineStep} from 'pipelines/PipelineTypes';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {assertNever} from 'util/Types';

import {AnalysisLayoutStatus} from './analysis-adapter/AnalysisAdapterTypes';
import getAnalysisLayoutStatusInfo from './analysis-adapter/getAnalysisLayoutStatusInfo';
import useHandleAnalysisLayoutEvents from './analysis-adapter/useHandleAnalysisLayoutEvents';
import DataLayout from './DataLayout';
import FailedLayout from './FailedLayout';
import {isLockedLayout} from './LayoutCreationUtils';
import RunningLayout from './RunningLayout';

export type AnalysisDataLayoutAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_LAYOUT>;

/**
 * Adapter between an analysis layout step and the actual data layout component.
 *
 * This component is responsible for handling analysis-specific concerns, such
 * as the status of the layout step.
 */
export default function AnalysisDataLayoutAdapter(props: AnalysisDataLayoutAdapterProps): JSX.Element {
  const layoutStep = getStepFromAnalysis(props.analysis, props.step.id);
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);

  const statusInfo = getAnalysisLayoutStatusInfo(props.analysis, layoutStep);

  if (statusInfo.status === AnalysisLayoutStatus.RUNNING) {
    return <RunningLayout message={statusInfo.message} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.FAILED) {
    return <FailedLayout message={statusInfo.message} reason={statusInfo.reason} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.READY) {
    return <ReadyAnalysisDataLayoutAdapter {...props} layoutStep={layoutStep} />;
  } else {
    throw new Error(`Unknown layout status: ${assertNever(statusInfo)}`);
  }
}

function getLayoutOverflowMenuOptions(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  analysisClientStateId: string,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>
): Array<OverflowMenuOption> {
  if (isAnalysisReadOnly(analysis)) {
    return [];
  }

  const {canEdit, message} = checkCanModifyStep(analysis, layoutStep.id, 'view', 'delete');

  return [
    {
      icon: 'delete',
      contents: <span>Delete view</span>,
      isDisabled: !canEdit,
      tooltipTitle: !canEdit ? message : undefined,
      onSelect: () =>
        confirmAndDeleteAnalysisView({
          analysis,
          stepId: layoutStep.id,
          stepType: PipelineStepType.LAYOUT,
          analysisClientStateId,
          typeName: 'view',
        }),
    },
  ];
}

function ReadyAnalysisDataLayoutAdapter({
  analysis,
  analysisClientStateId,
  clientStateId,
  layoutStep,
}: AnalysisDataLayoutAdapterProps & {layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>}): JSX.Element {
  const {handleOnEvents, layout} = useHandleAnalysisLayoutEvents({
    analysis,
    clientStateId,
    layoutStep,
  });

  const overflowMenuOptions = getLayoutOverflowMenuOptions(analysis, analysisClientStateId, layoutStep);

  const extraProps = isLockedLayout(layoutStep.config!)
    ? {
        additionalTitleComponents: (
          <ExperimentOptimizationFeedbackToolbar
            clientStateId={clientStateId}
            feedbackContext={{analysisId: analysis.id, layoutStepId: layoutStep.id}}
          />
        ),
      }
    : {};

  return (
    <DataLayout
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      clientId={layoutStep.id}
      layout={layout}
      onEvents={handleOnEvents}
      overflowMenuOptions={overflowMenuOptions}
      {...extraProps}
    />
  );
}
